import type { DiggerPose } from '@digger/shared';

/**
 * Top-right inset visualising the digger's dead-reckoned pose:
 *  - top-down dial: track base (rotated by heading) and cab+boom (rotated by
 *    heading+slew) — the cab/track misalignment is the thing that gets
 *    operators lost after slewing.
 *  - side profile: boom/dipper/bucket articulation from the same estimate.
 * Clicking the inset zeroes the estimate on the agent (dead reckoning
 * drifts; re-zero with the machine in a known attitude).
 */

// Side-view geometry (pixels in the local SVG frame).
const PIVOT = { x: 34, y: -20 }; // boom pivot on the cab
const BOOM_LEN = 34;
const DIPPER_LEN = 24;
const BUCKET_LEN = 11;

const DEG = Math.PI / 180;

/** Chain the arm segments; exported for tests. y is screen-down. */
export function armPoints(pose: DiggerPose): Array<{ x: number; y: number }> {
  const pts = [{ ...PIVOT }];
  let angle = pose.boomDeg;
  let { x, y } = PIVOT;
  for (const [len, rel] of [
    [BOOM_LEN, 0],
    [DIPPER_LEN, pose.dipperDeg],
    [BUCKET_LEN, pose.bucketDeg],
  ] as const) {
    angle += rel;
    x += Math.cos(angle * DEG) * len;
    y -= Math.sin(angle * DEG) * len;
    pts.push({ x, y });
  }
  return pts;
}

const fmt = (v: number) => `${Math.round(v)}°`;

export class PoseInset {
  private tracksEl: SVGGElement;
  private cabEl: SVGGElement;
  private armEl: SVGPolylineElement;
  private textEl: HTMLElement;
  private latest: DiggerPose | undefined;
  private frame: number | undefined;

  constructor(
    private readonly container: HTMLElement,
    /** Relay mode zeroes over signaling; LAN default POSTs the agent. */
    onReset?: () => void,
  ) {
    container.innerHTML = `
      <svg viewBox="0 0 132 196" width="150" aria-label="digger pose">
        <g transform="translate(66,58)">
          <circle r="50" fill="none" stroke="#2a333d" stroke-width="1.5"/>
          <g data-pose="tracks">
            <rect x="-19" y="-26" width="11" height="52" rx="3" fill="#3a4653"/>
            <rect x="8" y="-26" width="11" height="52" rx="3" fill="#3a4653"/>
            <path d="M0,-34 l6,9 h-12 z" fill="#6b7a89"/>
          </g>
          <g data-pose="cab">
            <rect x="-10" y="-10" width="20" height="24" rx="3" fill="#8b98a5"/>
            <rect x="-3" y="-44" width="6" height="34" rx="2" fill="#d7a13b"/>
            <circle cy="-12" r="2.4" fill="#7ee38b"/>
          </g>
        </g>
        <g transform="translate(6,178)">
          <line x1="0" y1="0" x2="120" y2="0" stroke="#2a333d" stroke-width="1.5"/>
          <rect x="10" y="-9" width="34" height="9" rx="4" fill="#3a4653"/>
          <rect x="14" y="-24" width="24" height="15" rx="2" fill="#8b98a5"/>
          <polyline data-pose="arm" points="" fill="none" stroke="#d7a13b"
                    stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
        </g>
      </svg>
      <div data-pose="text">pose: —</div>
      <div class="pose-hint">dead-reckoned · click to zero</div>`;

    this.tracksEl = container.querySelector('[data-pose="tracks"]')!;
    this.cabEl = container.querySelector('[data-pose="cab"]')!;
    this.armEl = container.querySelector('[data-pose="arm"]')!;
    this.textEl = container.querySelector('[data-pose="text"]')!;

    container.addEventListener('click', () => {
      if (onReset) onReset();
      else void fetch('/api/pose/reset', { method: 'POST' }).catch(() => {});
    });
  }

  /** Called per ack (~30 Hz); renders once per animation frame. */
  update(pose: DiggerPose): void {
    this.latest = pose;
    this.frame ??= requestAnimationFrame(() => {
      this.frame = undefined;
      if (this.latest) this.render(this.latest);
    });
  }

  private render(pose: DiggerPose): void {
    this.tracksEl.setAttribute('transform', `rotate(${pose.headingDeg})`);
    this.cabEl.setAttribute('transform', `rotate(${pose.headingDeg + pose.slewDeg})`);
    this.armEl.setAttribute(
      'points',
      armPoints(pose)
        .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(' '),
    );
    this.textEl.textContent =
      `hdg ${fmt(pose.headingDeg)} · slew ${fmt(pose.slewDeg)} · boom ${fmt(pose.boomDeg)}`;
  }
}
